import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LoadingBlock } from "@/components/Shared";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import AdminLayout from "@/layouts/AdminLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import ReportForm from "@/pages/ReportForm";
import History from "@/pages/History";
import ReportView from "@/pages/ReportView";
import Account from "@/pages/Account";
import Timesheet from "@/pages/Timesheet";
import Privacy from "@/pages/Privacy";
import AdminTimesheets from "@/pages/admin/AdminTimesheets";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReports from "@/pages/admin/AdminReports";
import Clients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import Projects from "@/pages/admin/Projects";
import ProjectForm from "@/pages/admin/ProjectForm";
import Employees from "@/pages/admin/Employees";
import Exports from "@/pages/admin/Exports";
import Settings from "@/pages/admin/Settings";

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen"><LoadingBlock /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ReportsAllowed({ children }) {
  const { user } = useAuth();
  if (user?.timesheet_only && user.role === "employee") return <Navigate to="/timesheet" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen"><LoadingBlock /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : user.timesheet_only && user.role === "employee" ? "/timesheet" : "/dashboard"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<Guard role="employee"><EmployeeLayout /></Guard>}>
            <Route path="/dashboard" element={<ReportsAllowed><EmployeeDashboard /></ReportsAllowed>} />
            <Route path="/reports/new" element={<ReportsAllowed><ReportForm /></ReportsAllowed>} />
            <Route path="/reports/:id/edit" element={<ReportsAllowed><ReportForm /></ReportsAllowed>} />
            <Route path="/reports/:id" element={<ReportsAllowed><ReportView /></ReportsAllowed>} />
            <Route path="/history" element={<ReportsAllowed><History /></ReportsAllowed>} />
            <Route path="/timesheet" element={<Timesheet />} />
            <Route path="/account" element={<Account />} />
          </Route>
          <Route element={<Guard role="admin"><AdminLayout /></Guard>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/reports/:id" element={<ReportView admin />} />
            <Route path="/admin/reports/:id/edit" element={<ReportForm admin />} />
            <Route path="/admin/clients" element={<Clients />} />
            <Route path="/admin/clients/:id" element={<ClientDetail />} />
            <Route path="/admin/projects" element={<Projects />} />
            <Route path="/admin/projects/new" element={<ProjectForm />} />
            <Route path="/admin/projects/:id" element={<ProjectForm />} />
            <Route path="/admin/employees" element={<Employees />} />
            <Route path="/admin/exports" element={<Exports />} />
            <Route path="/admin/timesheets" element={<AdminTimesheets />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/admin/account" element={<Account />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </AuthProvider>
  );
}
